import { Injectable, Inject, InjectionToken, Optional, ComponentRef, OnDestroy } from '@angular/core';
import {
  TNotification,
  ISimpleNotification,
  IComponentNotification,
  INotificationsConfig,
  INotificationInstance,
  INotificationCloseResolve,
  TNotificationCloseData,
} from '../interface';
import { DynamicBodyComponentService } from '../../../dynamic';
import { Store, StoreReadonly } from '../../../../shared/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { NotificationsContainerComponent } from '../component/notifications-container/notifications-container';

export const NOTIFICATIONS_SERVICE_CONFIG = new InjectionToken<INotificationsConfig>('vim.layout.notifications.config');

export const STORE_INITIAL_STATE = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notifications: [] as INotificationInstance<any>[],
};

const NOTIFICATIONS_SERVICE_CONFIG_DEFAULT: INotificationsConfig = {
  showTime: 5,
  maxOnScreen: 5,
  isFullSize: false,
};

// eslint-disable-next-line @typescript-eslint/naming-convention
enum ENotificationStyleOffset {
  offset8Px = 8,
  offset64Px = 64,
  offset128Px = 128,
}

// eslint-disable-next-line @typescript-eslint/naming-convention
interface INotificationResetParams {
  resetBottomPosition?: boolean;
  resetTopPosition?: boolean;
}

@Injectable({ providedIn: 'root' })
export class VimNotificationsService implements OnDestroy {
  private store = new Store<typeof STORE_INITIAL_STATE>(STORE_INITIAL_STATE);
  private styleBottomPx = new BehaviorSubject<number | null>(ENotificationStyleOffset.offset8Px);
  private styleTopPx = new BehaviorSubject<number | null>(null);

  store$: StoreReadonly<typeof STORE_INITIAL_STATE> = this.store.getReadonly();
  visibleNotifications$: Observable<INotificationInstance[]>;
  styleBottomPx$ = this.styleBottomPx.asObservable();
  styleTopPx$ = this.styleTopPx.asObservable();

  private notificationsContainerComponentRef: ComponentRef<NotificationsContainerComponent>;

  constructor(
    @Optional() @Inject(NOTIFICATIONS_SERVICE_CONFIG) private config: INotificationsConfig,
    private dynamicBodyComponentService: DynamicBodyComponentService,
  ) {
    this.config = this.config || NOTIFICATIONS_SERVICE_CONFIG_DEFAULT;

    this.visibleNotifications$ = this.store$
      .select('notifications')
      .pipe(map(notifications => this.sliceVisibleNotifications(notifications)));

    // NOTE: cant be attached right on application start
    window.setTimeout(() => this.attachNotificationsContainer());
  }

  ngOnDestroy(): void {
    this.detachNotificationsContainer();
  }

  add<T extends Object | void = void>(notification: ISimpleNotification): INotificationInstance<T>;
  add<T extends Object | void = void>(notification: IComponentNotification): INotificationInstance<T>;
  add<T extends Object | void = void>(notification: TNotification): INotificationInstance<T> {
    let closeResolve: INotificationCloseResolve<T>;
    const closePromise = new Promise<TNotificationCloseData<T>>(
      resolve => (closeResolve = resolve as INotificationCloseResolve<T>),
    );

    const notificationInstance: INotificationInstance<T> = {
      closePromise: closePromise,
      closeResolve: closeResolve!,
      data: notification,
    };

    this.storeAddNotification(notificationInstance);

    return notificationInstance;
  }

  close<T extends Object | void = void>(
    notification: INotificationInstance<T>,
    closeData?: TNotificationCloseData<T>,
  ): void {
    notification.closeResolve(closeData);

    this.storeRemoveNotification(notification);
  }

  closeAllVisibleNotifications(): void {
    this.visibleNotifications$
      .pipe(take(1))
      .subscribe(notifications => notifications.forEach(notification => this.close(notification)));
  }

  getConfig(): INotificationsConfig {
    return this.config;
  }

  raiseNotificationPosition(): void {
    this.styleBottomPx.next(ENotificationStyleOffset.offset128Px);
    this.resetOppositeOffset({ resetTopPosition: true });
  }

  lowerNotificationPosition(): void {
    this.styleBottomPx.next(ENotificationStyleOffset.offset8Px);
    this.resetOppositeOffset({ resetTopPosition: true });
  }

  highNotificationPosition(): void {
    this.styleTopPx.next(ENotificationStyleOffset.offset8Px);
    this.resetOppositeOffset({ resetBottomPosition: true });
  }

  private resetOppositeOffset(params?: INotificationResetParams): void {
    if (params?.resetBottomPosition) {
      this.styleBottomPx.next(null);
    }
    if (params?.resetTopPosition) {
      this.styleTopPx.next(null);
    }
  }

  private sliceVisibleNotifications<T extends Object | void = void>(
    notifications: INotificationInstance<T>[],
  ): INotificationInstance<T>[] {
    const maxOnScreen = this.getConfig().maxOnScreen;

    return notifications.slice(0, maxOnScreen);
  }

  private storeAddNotification<T extends Object | void = void>(notification: INotificationInstance<T>): void {
    this.store.update(state => {
      const notifications = [...state.notifications, notification];

      return { ...state, notifications };
    });
  }

  private storeRemoveNotification<T extends Object | void = void>(
    removingNotification: INotificationInstance<T>,
  ): void {
    this.store.update(state => {
      const notifications = state.notifications.filter(notification => notification !== removingNotification);

      return { ...state, notifications };
    });
  }

  private attachNotificationsContainer(): void {
    this.notificationsContainerComponentRef = this.dynamicBodyComponentService.attachComponent(
      NotificationsContainerComponent,
    );
  }

  private detachNotificationsContainer(): void {
    this.dynamicBodyComponentService.detachComponent(this.notificationsContainerComponentRef);
  }
}
